// MetricModal "Konum" seçicisi (spec §6.4, §18 DR14; Modal.dc.html radyo düzeni). Önce radyo:
// "Bir kategori altında" (yalnız kategoriler — aranabilir seçici) / "Bir göstergenin alt kalemi"
// (gösterge araması: yol ipucu, ilk 50 + "aramayı daraltın"; seçilen göstergenin grupları radyo +
// "Yeni grup…" → BreakdownGroupModal) / "Kök" (kök = kategori → yalnız Sistem Yöneticisi). Üye
// kipinde `dimensionMember` etiketten türetilir (elle düzenlenebilir). Grup düğümünde konum sabit.
// Kural saf lib/kpi-catalog-breakdown; kipin kendisi MetricModal'da (birim bölümü ona bakar).

import { useEffect, useMemo, useState } from "react";
import type { KpiMetric } from "@mahir/shared";
import { KPI_BREAKDOWN_COPY } from "../../lib/kpi-breakdown-copy";
import {
  catalogPathOf,
  categoryParentOptions,
  memberSlugFor,
  memberTotalOptions,
  MEMBER_SLUG_RE,
  TOTAL_SEARCH_LIMIT,
  totalGroups,
  type CatalogView,
  type LocationMode,
} from "../../lib/kpi-catalog-breakdown";
import { type MetricDraft } from "../../lib/kpi-metric-draft";
import { SUPERADMIN_ONLY_HINT } from "../../lib/role-ui";
import { SearchSelect } from "../common/SearchSelect";
import { btnSmall, fieldStyle, inputStyle, labelStyle, monoStyle } from "./kpi-form-styles";

const C = KPI_BREAKDOWN_COPY.location;
const help = { fontSize: "0.875rem", color: "var(--color-text-3)" } as const;
const radioRow = { display: "flex", gap: 10, alignItems: "flex-start", padding: "6px 0" } as const;
const radioInput = { marginTop: 4, width: 18, height: 18 } as const;

export interface MetricModalLocationProps {
  metric: KpiMetric | null;
  catalog: readonly KpiMetric[];
  view: CatalogView;
  kind: "metric" | "category" | "group";
  draft: MetricDraft;
  mode: LocationMode;
  disabled: boolean;
  /** kök seçeneği (kök = kategori — `kpi.catalog.categories`) */
  rootAllowed: boolean;
  set: <K extends keyof MetricDraft>(k: K, v: MetricDraft[K]) => void;
  onMode: (mode: LocationMode) => void;
  /** "Yeni grup…" — seçili toplam için BreakdownGroupModal (üst bileşen) */
  onNewGroup?: ((totalId: string) => void) | undefined;
}

export function MetricModalLocation(props: MetricModalLocationProps): JSX.Element {
  const { metric, catalog, view, draft, mode, set } = props;
  const byId = useMemo(() => new Map(catalog.map((m) => [m.id, m])), [catalog]);
  const isMemberNow = metric?.dimensionMember != null;
  const parentIsGroup =
    draft.parentId !== null && view.kindOf(draft.parentId) === "breakdown_group";
  // üye kipinde seçili toplam: üst grup ise grubun üstü; yoksa yerel seçim
  const [pickedTotal, setPickedTotal] = useState<string | null>(null);
  const totalId = parentIsGroup ? (byId.get(draft.parentId!)?.parentId ?? null) : pickedTotal;
  const [slugTouched, setSlugTouched] = useState(metric !== null);
  const autoSlug = memberSlugFor(draft.label);
  useEffect(() => {
    // `set` kararlı (MetricModal useCallback); aynı değerde yazım yok — döngü olmaz
    if (mode === "member" && !slugTouched && draft.dimensionMember !== autoSlug) {
      set("dimensionMember", autoSlug);
    }
  }, [mode, slugTouched, autoSlug, draft.dimensionMember, set]);

  if (props.kind === "group") {
    const total = draft.parentId ? byId.get(draft.parentId) : undefined;
    return (
      <div style={{ ...fieldStyle, ...help }} data-testid="kpi-metric-location-fixed">
        <span style={labelStyle}>{C.legend}</span>
        {C.groupParent(total?.label ?? "—")}
      </div>
    );
  }

  const dis = props.disabled;
  const allowMember = props.kind === "metric";
  const memberBlocked = metric !== null && view.childrenOf(metric.id).length > 0;
  const moveLocked = isMemberNow; // üye grubundan ayrılamaz (breakdown_member_move)

  function choose(next: LocationMode): void {
    if (next === mode) return;
    props.onMode(next);
    if (next === "root") {
      set("parentId", null);
      set("dimensionMember", null);
    } else if (next === "category") {
      set("dimensionMember", null);
      if (parentIsGroup) set("parentId", null);
    } else {
      if (!parentIsGroup) set("parentId", null);
      set("dimensionMember", memberSlugFor(draft.label));
      setSlugTouched(false);
    }
  }

  const option = (
    value: LocationMode,
    label: string,
    opts: { disabled?: boolean; hint?: string | null } = {},
  ): JSX.Element => (
    <label
      key={value}
      style={{
        ...radioRow,
        color: opts.disabled ? "var(--color-text-3)" : "var(--color-text)",
      }}
      title={opts.disabled && opts.hint ? opts.hint : undefined}
    >
      <input
        type="radio"
        name="m-location"
        value={value}
        checked={mode === value}
        disabled={dis || opts.disabled === true || (moveLocked && value !== "member")}
        style={radioInput}
        onChange={() => choose(value)}
      />
      <span>
        {label}
        {opts.hint ? <span style={help}> — {opts.hint}</span> : null}
      </span>
    </label>
  );

  return (
    <fieldset
      style={{ border: 0, padding: 0, margin: "0 0 12px" }}
      data-testid="kpi-metric-location"
    >
      <legend style={labelStyle}>{C.legend}</legend>
      {option("category", C.category)}
      {allowMember
        ? option("member", C.member, {
            disabled: memberBlocked,
            hint: memberBlocked ? C.memberDepth : null,
          })
        : null}
      {option("root", C.root, {
        disabled: !props.rootAllowed,
        hint: props.rootAllowed ? C.rootHint : SUPERADMIN_ONLY_HINT,
      })}
      {moveLocked ? <div style={help}>{C.memberLocked}</div> : null}

      {mode === "category" ? (
        <div style={{ ...fieldStyle, marginTop: 6 }}>
          <label style={labelStyle} htmlFor="m-parent">
            {C.categoryLabel}
          </label>
          <SearchSelect
            id="m-parent"
            ariaLabel={C.categoryLabel}
            value={draft.parentId ?? ""}
            disabled={dis}
            placeholder={C.categoryPlaceholder}
            searchPlaceholder={C.categorySearch}
            showCode={false}
            options={categoryParentOptions(catalog, view, metric, draft.parentId)}
            onChange={(v) => set("parentId", v || null)}
          />
        </div>
      ) : null}

      {mode === "member" ? (
        <MemberFields
          {...props}
          byId={byId}
          totalId={totalId}
          locked={dis || moveLocked}
          onTotal={(id) => {
            setPickedTotal(id);
            set("parentId", null);
          }}
          onSlug={(v) => {
            setSlugTouched(true);
            set("dimensionMember", v);
          }}
        />
      ) : null}
    </fieldset>
  );
}

function MemberFields(
  props: MetricModalLocationProps & {
    byId: ReadonlyMap<string, KpiMetric>;
    totalId: string | null;
    locked: boolean;
    onTotal: (id: string | null) => void;
    onSlug: (slug: string) => void;
  },
): JSX.Element {
  const { catalog, view, metric, draft, totalId, set } = props;
  const totals = useMemo(() => memberTotalOptions(catalog, view, metric), [catalog, view, metric]);
  const groups = totalGroups(view, totalId);
  const slug = draft.dimensionMember ?? "";
  const slugBad = slug !== "" && !MEMBER_SLUG_RE.test(slug);
  return (
    <div className="grid gap-2" style={{ marginTop: 6 }}>
      <div style={fieldStyle}>
        <label style={labelStyle} htmlFor="m-total">
          {C.totalLabel}
        </label>
        <SearchSelect
          id="m-total"
          ariaLabel={C.totalLabel}
          value={totalId ?? ""}
          disabled={props.locked}
          placeholder={C.totalPlaceholder}
          searchPlaceholder={C.totalSearch}
          showCode={false}
          limit={TOTAL_SEARCH_LIMIT}
          moreText={C.narrow}
          options={totals}
          onChange={(v) => props.onTotal(v || null)}
        />
        {totalId ? (
          <span style={help} data-testid="kpi-metric-location-path">
            {catalogPathOf(props.byId, totalId)}
          </span>
        ) : null}
      </div>
      {totalId ? (
        <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
          <legend style={labelStyle}>{C.groupLegend}</legend>
          {groups.length === 0 ? <div style={help}>{C.noGroups}</div> : null}
          {groups.map((g) => (
            <label key={g.id} style={radioRow}>
              <input
                type="radio"
                name="m-group"
                value={g.id}
                checked={draft.parentId === g.id}
                disabled={props.locked}
                style={radioInput}
                onChange={() => set("parentId", g.id)}
              />
              <span>
                {g.label}
                <span style={help}>
                  {" "}
                  ·{" "}
                  {KPI_BREAKDOWN_COPY.catalog.groupLine(
                    view.membersOf(g.id).length,
                    g.breakdown?.complete ?? true,
                    g.breakdown?.rollup ?? "sum",
                  )}
                </span>
              </span>
            </label>
          ))}
          {props.onNewGroup ? (
            <button
              type="button"
              style={{ ...btnSmall, minHeight: 44, marginTop: 4 }}
              disabled={props.locked}
              data-testid="kpi-metric-location-new-group"
              onClick={() => props.onNewGroup?.(totalId)}
            >
              {C.newGroup}
            </button>
          ) : null}
        </fieldset>
      ) : null}
      <div style={fieldStyle}>
        <label style={labelStyle} htmlFor="m-member-slug">
          {C.memberSlug}
        </label>
        <input
          id="m-member-slug"
          style={{
            ...inputStyle,
            ...monoStyle,
            ...(slugBad ? { borderColor: "var(--color-error)" } : {}),
          }}
          value={slug}
          maxLength={64}
          spellCheck={false}
          disabled={props.disabled}
          aria-invalid={slugBad || undefined}
          onChange={(e) => props.onSlug(e.target.value)}
        />
        <span style={slugBad ? { ...help, color: "var(--color-error)" } : help}>
          {slugBad ? C.memberSlugInvalid : C.memberSlugHint}
        </span>
      </div>
    </div>
  );
}
